import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {buildHoverParentClassName} from '../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {buildContactDisplayName} from '../../../helpers/models/contactHelpers';
import {ParticipantTypesEnum} from '../../../models/api/participantModel';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';
import {ContactCardLocationsEnum} from '../../../store/panels/contacts/contactCard/contactCardPanelState';
import {CalendarsPanelId} from '../../../store/panels/panelStateBase';
import {ContactAvatarWithResponseStatus} from '../../common/avatars/contactAvatarWithResponseStatus';
import {ContactCardWrapperContainer} from '../../contactCard/contactCardWrapperContainer';

/*
 * Props.
 */

interface CalendarEventParticipantsItemProps {
  panelId: CalendarsPanelId;
  visualParticipant: VisualParticipantModel;
  onRemove?: Handler;
  onTypeChange?: HandlerOf<ParticipantTypesEnum>;
  shouldDisableTypeButton?: boolean;
  isInEventEditor?: boolean;
}

/*
 * Styles.
 */

interface ActionsWrapperStyleProps {
  $isOpen: boolean;
}

const StyledActionsWrapperDiv = styled.div<ActionsWrapperStyleProps>`
  position: absolute;
  top: 0px;
  right: 0px;
  bottom: 0px;
  padding-left: 38px;
  padding-right: 2px;
  display: flex;
  flex-direction: row;
  align-items: center;
  background: ${(p) =>
    `linear-gradient(to right, transparent 0px, ${getBackgroundColor(
      p.$isOpen,
      p.theme,
    )} 30px, ${getBackgroundColor(p.$isOpen, p.theme)} 100%)`};
  visibility: ${(p) => (p.$isOpen ? 'visible' : 'hidden')};
`;

function getBackgroundColor(isOpen: boolean, theme: UserTheme) {
  return isOpen ? theme.greys.shade40 : theme.greys.shade30;
}

const StyledIconButton = styled(IconButton)`
  padding: 0px;
  margin-right: 6px;

  &:hover {
    background: none;
  }
`;

const StyledParticipantDiv = styled.div<ActionsWrapperStyleProps>`
  position: relative;
  display: grid;
  grid-template-columns: 20px auto;
  grid-column-gap: 12px;
  padding: 5px 8px;
  border-radius: 4px;

  background-color: ${(p) => (p.$isOpen ? p.theme.greys.shade40 : p.theme.greys.white)};
  overflow: hidden;

  &:hover {
    background-color: ${(p) => p.theme.greys.shade30};

    ${StyledActionsWrapperDiv} {
      visibility: visible;
    }
  }
`;

const StyledParticipantTextDiv = styled.div`
  ${ellipsis()}
`;

const StyledParticipantDescriptorSpan = styled.span`
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const CalendarEventParticipantsItem: FC<CalendarEventParticipantsItemProps> = (props) => (
  <PopoverCoordinator
    renderButton={(isOpen) => renderParticipantContent(isOpen, props)}
    renderModal={(modalProps) =>
      renderContactDropdown(modalProps, props.panelId, props.visualParticipant, props.isInEventEditor)
    }
  />
);

function renderParticipantContent(isOpen: boolean, props: CalendarEventParticipantsItemProps) {
  const {visualParticipant} = props;

  return (
    <StyledParticipantDiv $isOpen={isOpen} className={buildHoverParentClassName()}>
      <ContactAvatarWithResponseStatus
        visualParticipant={visualParticipant}
        size={VisualSizesEnum.MEDIUM}
        isOpen={isOpen}
      />
      <StyledParticipantTextDiv>
        {renderParticipantName(visualParticipant)}{' '}
        <StyledParticipantDescriptorSpan>
          {maybeRenderParticipantDescriptor(visualParticipant)}
        </StyledParticipantDescriptorSpan>
      </StyledParticipantTextDiv>
      {maybeRenderActionButtons(isOpen, props)}
    </StyledParticipantDiv>
  );
}

function maybeRenderActionButtons(isOpen: boolean, props: CalendarEventParticipantsItemProps) {
  const {onTypeChange, onRemove} = props;

  // Don't render any buttons unless we have handlers.
  if (!onTypeChange || !onRemove) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledActionsWrapperDiv $isOpen={isOpen}>
          {maybeRenderTypeButton(props, onTypeChange)}
          <StyledIconButton
            iconName="clearSmall"
            label={<Trans id="calendar-participant-remove">Remove</Trans>}
            color={theme.greys.shade60}
            hoverColor={theme.greys.shade70}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          />
        </StyledActionsWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderTypeButton(
  props: CalendarEventParticipantsItemProps,
  onTypeChange: HandlerOf<ParticipantTypesEnum>,
) {
  if (props.shouldDisableTypeButton) {
    return null;
  }

  const {type} = props.visualParticipant;

  // Resource participants like meeting rooms cannot be marked as optional.
  if (type === ParticipantTypesEnum.RESOURCE) {
    return null;
  }

  const isRequired = type === ParticipantTypesEnum.REQUIRED;

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledIconButton
          iconName={isRequired ? 'requiredGuestSmall' : 'optionalGuestSmall'}
          label={
            isRequired ? (
              <Trans id="calendar-participant-mark-optional">Mark optional</Trans>
            ) : (
              <Trans id="calendar-participant-mark-required">Mark required</Trans>
            )
          }
          color={theme.greys.shade60}
          hoverColor={theme.greys.shade70}
          onClick={(e) => {
            e.stopPropagation();
            onTypeChange(isRequired ? ParticipantTypesEnum.OPTIONAL : ParticipantTypesEnum.REQUIRED);
          }}
        />
      )}
    </UserThemeConsumer>
  );
}

function renderContactDropdown(
  modalProps: PopoverTransferProps,
  panelId: CalendarsPanelId,
  visualParticipant: VisualParticipantModel,
  isInEventEditor?: boolean,
) {
  return (
    <ContactCardWrapperContainer
      visualContact={visualParticipant}
      panelId={panelId}
      popoverProps={modalProps}
      location={
        isInEventEditor ? ContactCardLocationsEnum.CALENDAR_NEW_EVENT : ContactCardLocationsEnum.CALENDAR
      }
    />
  );
}

function renderParticipantName(participant: VisualParticipantModel) {
  return buildContactDisplayName(participant);
}

function maybeRenderParticipantDescriptor(participant: VisualParticipantModel) {
  if (participant.isOrganizer) {
    return <Trans id="participant-item-organizer-descriptor">(organizer)</Trans>;
  }

  // When we add backend support for optional attendees, we'll add a case here for "optional".

  return null;
}
